import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, Pencil, Trash2, UploadCloud, X, Eye, EyeOff, PackagePlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  getAdminProducts,
  createProduct,
  updateProduct,
  setProductVisibility,
  deleteProduct,
  resolveImageUrl,
} from "@/lib/api";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_SIZE = 8 * 1024 * 1024;

const CATEGORIES = [
  { id: "obleceni", label: "Oblečení" },
  { id: "samolepky", label: "Samolepky" },
  { id: "doplnky", label: "Doplňky" },
];

const czk = (n) => `${Number(n || 0).toLocaleString("cs-CZ")} Kč`;

const EMPTY_FORM = {
  name: "",
  description: "",
  price: "",
  category: "obleceni",
  sizes: "",
  badge: "",
};

const ProductFields = ({ form, setForm, idPrefix }) => {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <Input
        data-testid={`${idPrefix}-name-input`}
        placeholder="Název produktu *"
        value={form.name}
        onChange={set("name")}
        className="h-11 border-white/15 bg-[#0B0D10]"
      />
      <Textarea
        data-testid={`${idPrefix}-description-input`}
        placeholder="Popis produktu"
        value={form.description}
        onChange={set("description")}
        className="min-h-20 border-white/15 bg-[#0B0D10]"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          data-testid={`${idPrefix}-price-input`}
          type="number"
          min="0"
          placeholder="Cena v Kč *"
          value={form.price}
          onChange={set("price")}
          className="h-11 border-white/15 bg-[#0B0D10] font-mono"
        />
        <Select
          value={form.category}
          onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
        >
          <SelectTrigger
            data-testid={`${idPrefix}-category-select`}
            className="h-11 border-white/15 bg-[#0B0D10]"
          >
            <SelectValue placeholder="Kategorie" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0F1217]">
            {CATEGORIES.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`${idPrefix}-category-option-${c.id}`}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          data-testid={`${idPrefix}-sizes-input`}
          placeholder="Velikosti (S, M, L, XL)"
          value={form.sizes}
          onChange={set("sizes")}
          className="h-11 border-white/15 bg-[#0B0D10] font-mono"
        />
        <Input
          data-testid={`${idPrefix}-badge-input`}
          placeholder="Badge (např. LIMITKA)"
          value={form.badge}
          onChange={set("badge")}
          className="h-11 border-white/15 bg-[#0B0D10]"
        />
      </div>
    </>
  );
};

const buildFormData = (form, file) => {
  const fd = new FormData();
  if (file) fd.append("file", file);
  fd.append("name", form.name.trim());
  fd.append("description", form.description.trim());
  fd.append("price", String(parseInt(form.price, 10)));
  fd.append("category", form.category);
  fd.append("sizes", form.sizes.trim());
  fd.append("badge", form.badge.trim());
  return fd;
};

const validate = (form) => {
  if (!form.name.trim()) return "Vyplň název produktu.";
  const price = parseInt(form.price, 10);
  if (Number.isNaN(price) || price < 0) return "Zadej platnou cenu.";
  return null;
};

export const ProductManager = ({ onChanged }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // create form
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // edit dialog
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const editFileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProducts(await getAdminProducts());
    } catch {
      // 401 handled globally
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
      if (editPreview) URL.revokeObjectURL(editPreview);
    };
  }, [preview, editPreview]);

  const refresh = async () => {
    await load();
    onChanged?.();
  };

  const checkFile = (f) => {
    if (!f) return false;
    if (!ALLOWED_TYPES.includes(f.type)) {
      toast.error("Nepodporovaný formát. Povolené: JPG, PNG, WEBP, GIF.");
      return false;
    }
    if (f.size > MAX_SIZE) {
      toast.error("Soubor je příliš velký (max 8 MB).");
      return false;
    }
    return true;
  };

  const acceptFile = (f) => {
    if (!checkFile(f)) return;
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const acceptEditFile = (f) => {
    if (!checkFile(f)) return;
    if (editPreview) URL.revokeObjectURL(editPreview);
    setEditFile(f);
    setEditPreview(URL.createObjectURL(f));
  };

  const clearFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Vyber fotku produktu.");
      return;
    }
    const err = validate(form);
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await createProduct(buildFormData(form, file));
      toast.success("Produkt přidán do shopu!");
      setForm(EMPTY_FORM);
      clearFile();
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Produkt se nepodařilo uložit.");
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (p) => {
    setEditProduct(p);
    setEditForm({
      name: p.name,
      description: p.description || "",
      price: String(p.price),
      category: p.category,
      sizes: (p.sizes || []).join(", "),
      badge: p.badge || "",
    });
    setEditFile(null);
    setEditPreview(null);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const err = validate(editForm);
    if (err) {
      toast.error(err);
      return;
    }
    setEditSaving(true);
    try {
      await updateProduct(editProduct.id, buildFormData(editForm, editFile));
      toast.success("Změny uloženy.");
      setEditProduct(null);
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Změny se nepodařilo uložit.");
    } finally {
      setEditSaving(false);
    }
  };

  const toggleVisibility = async (p) => {
    try {
      await setProductVisibility(p.id, !p.visible);
      toast.success(p.visible ? "Produkt skryt z veřejného shopu." : "Produkt je znovu viditelný.");
      await refresh();
    } catch {
      toast.error("Viditelnost se nepodařilo změnit.");
    }
  };

  const remove = async (productId) => {
    try {
      await deleteProduct(productId);
      toast.success("Produkt smazán.");
      await refresh();
    } catch {
      toast.error("Produkt se nepodařilo smazat.");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
      {/* create form */}
      <div className="lg:col-span-5">
        <form
          data-testid="admin-product-form"
          onSubmit={submit}
          className="clip-corner space-y-4 border border-white/10 bg-[#0F1217] p-6"
        >
          <div className="flex items-center gap-3">
            <div className="clip-corner-sm flex h-10 w-10 items-center justify-center bg-[#B7FF2A]/10 text-[#B7FF2A]">
              <PackagePlus className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-xl uppercase tracking-[0.06em]">Přidat produkt</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                Nový produkt se hned zobrazí v shopu
              </div>
            </div>
          </div>

          {/* dropzone / preview */}
          {preview ? (
            <div className="clip-corner-sm relative overflow-hidden border border-[#B7FF2A]/30">
              <img
                data-testid="admin-product-preview"
                src={preview}
                alt="Náhled"
                className="aspect-video w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-[#07080A]/80 px-3 py-2">
                <span className="truncate font-mono text-[11px] text-white/70">
                  {file?.name} · {(file?.size / 1024 / 1024).toFixed(1)} MB
                </span>
                <button
                  type="button"
                  data-testid="admin-product-preview-remove"
                  onClick={clearFile}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/5 text-white/60 transition-colors hover:bg-[#FF3B3B] hover:text-white"
                  aria-label="Odebrat soubor"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <div
              data-testid="admin-product-dropzone"
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                acceptFile(e.dataTransfer.files?.[0]);
              }}
              className={`clip-corner-sm flex cursor-pointer flex-col items-center justify-center gap-3 border-2 border-dashed px-6 py-8 text-center transition-colors duration-200 ${
                dragActive
                  ? "border-[#B7FF2A] bg-[#B7FF2A]/10"
                  : "border-white/15 bg-[#0B0D10] hover:border-[#B7FF2A]/50 hover:bg-[#B7FF2A]/5"
              }`}
            >
              <UploadCloud className={`h-8 w-8 ${dragActive ? "text-[#B7FF2A]" : "text-white/30"}`} />
              <div>
                <div className="font-display text-lg uppercase tracking-[0.08em] text-white/80">
                  Přetáhni fotku produktu
                </div>
                <div className="mt-1 font-mono text-[11px] text-white/40">
                  nebo klikni pro výběr · JPG / PNG / WEBP / GIF · max 8 MB
                </div>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            data-testid="admin-product-file-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => acceptFile(e.target.files?.[0])}
          />

          <ProductFields form={form} setForm={setForm} idPrefix="admin-product" />

          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-product-submit-button"
            className="light-sweep clip-corner-sm h-12 w-full bg-[#B7FF2A] font-display text-base uppercase tracking-[0.14em] text-[#07080A] hover:bg-[#B7FF2A] hover:brightness-110 disabled:opacity-60"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Ukládám…
              </span>
            ) : (
              "Přidat do shopu"
            )}
          </Button>
        </form>
      </div>

      {/* product list */}
      <div className="lg:col-span-7">
        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="admin-products-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="clip-corner-sm aspect-square animate-pulse border border-white/5 bg-[#0F1217]" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="py-14 text-center text-white/40" data-testid="admin-products-empty">
            Shop je prázdný. Přidej první produkt.
          </div>
        ) : (
          <div data-testid="admin-products-list" className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {products.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-product-card-${p.id}`}
                className={`clip-corner-sm group relative overflow-hidden border bg-[#0F1217] transition-colors duration-200 ${
                  p.visible === false ? "border-[#FF3B3B]/30" : "border-white/10"
                }`}
              >
                <img
                  src={resolveImageUrl(p.image)}
                  alt={p.name}
                  className={`aspect-square w-full object-cover ${p.visible === false ? "opacity-40 grayscale" : ""}`}
                  loading="lazy"
                />
                {p.visible === false && (
                  <span
                    data-testid={`admin-product-hidden-badge-${p.id}`}
                    className="absolute left-2 top-2 flex items-center gap-1 bg-[#FF3B3B] px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-white"
                  >
                    <EyeOff className="h-3 w-3" /> Skryto
                  </span>
                )}
                {p.badge && p.visible !== false && (
                  <span className="absolute left-2 top-2 bg-[#B7FF2A] px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[#07080A]">
                    {p.badge}
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#07080A]/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="truncate font-display text-sm uppercase tracking-[0.06em] text-white">{p.name}</div>
                  <div className="font-mono text-xs text-[#B7FF2A]">{czk(p.price)}</div>
                </div>
                <div className="absolute right-2 top-2 flex gap-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                  <button
                    data-testid={`admin-product-visibility-toggle-${p.id}`}
                    onClick={() => toggleVisibility(p)}
                    className="flex h-8 w-8 items-center justify-center rounded-md bg-[#07080A]/70 text-white/60 transition-colors duration-200 hover:bg-[#B7FF2A] hover:text-[#07080A]"
                    aria-label={p.visible === false ? "Zobrazit produkt" : "Skrýt produkt"}
                    title={p.visible === false ? "Zobrazit produkt" : "Skrýt produkt"}
                  >
                    {p.visible === false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    data-testid={`admin-product-edit-${p.id}`}
                    onClick={() => openEdit(p)}
                    className="flex h-8 w-8 items-center justify-center rounded-md bg-[#07080A]/70 text-white/60 transition-colors duration-200 hover:bg-[#1EA7FF] hover:text-[#07080A]"
                    aria-label="Upravit produkt"
                    title="Upravit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`admin-product-delete-${p.id}`}
                    onClick={() => remove(p.id)}
                    className="flex h-8 w-8 items-center justify-center rounded-md bg-[#07080A]/70 text-white/60 transition-colors duration-200 hover:bg-[#FF3B3B] hover:text-white"
                    aria-label="Smazat produkt"
                    title="Smazat"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* edit dialog */}
      <Dialog open={!!editProduct} onOpenChange={(open) => !open && setEditProduct(null)}>
        <DialogContent
          data-testid="admin-product-edit-dialog"
          aria-describedby={undefined}
          className="max-h-[90vh] max-w-lg overflow-y-auto border-white/10 bg-[#0F1217]"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-2xl uppercase tracking-[0.08em] text-white">
              <Pencil className="h-5 w-5 text-[#1EA7FF]" /> Upravit produkt
            </DialogTitle>
          </DialogHeader>
          {editProduct && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div
                className="clip-corner-sm group/img relative cursor-pointer overflow-hidden border border-white/10"
                onClick={() => editFileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") editFileInputRef.current?.click();
                }}
                data-testid="admin-product-edit-image"
              >
                <img
                  src={editPreview || resolveImageUrl(editProduct.image)}
                  alt={editProduct.name}
                  className="aspect-video w-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-[#07080A]/0 opacity-0 transition-opacity duration-200 group-hover/img:bg-[#07080A]/60 group-hover/img:opacity-100">
                  <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-white">
                    <UploadCloud className="h-4 w-4" /> Vyměnit fotku
                  </span>
                </div>
              </div>
              <input
                ref={editFileInputRef}
                data-testid="admin-product-edit-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => acceptEditFile(e.target.files?.[0])}
              />

              <ProductFields form={editForm} setForm={setEditForm} idPrefix="admin-product-edit" />

              <div className="flex gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="admin-product-edit-cancel-button"
                  onClick={() => setEditProduct(null)}
                  className="clip-corner-sm flex-1 border-white/20 font-display uppercase tracking-[0.12em] hover:bg-white/5"
                >
                  Zrušit
                </Button>
                <Button
                  type="submit"
                  disabled={editSaving}
                  data-testid="admin-product-edit-save-button"
                  className="light-sweep clip-corner-sm flex-1 bg-[#1EA7FF] font-display uppercase tracking-[0.12em] text-[#07080A] hover:bg-[#1EA7FF] hover:brightness-110 disabled:opacity-60"
                >
                  {editSaving ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Ukládám…
                    </span>
                  ) : (
                    "Uložit změny"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
